import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { SearchFiltersSchema, SearchQuery } from '@/sources/types';

/**
 * URL-state contract for the search UI. We encode a `SearchQuery` as a flat
 * set of query params:
 *
 *   q       — free-form keyword (maps to `SearchQuery.q`)
 *   uploader — uploader/submitter browse, for sources that support it
 *   sort    — sort key
 *   lang    — language filter
 *   inc     — comma-separated include tags (`tag:big breasts,artist:foo`)
 *   exc     — comma-separated exclude tags
 *   f.<id>  — per-source extra filter values; comma-separated for multi
 *
 * Empty / absent params produce an empty `SearchQuery` (which the source
 * page treats as "popular").
 */

const FIELD_PREFIX = 'f.';

const RESERVED_KEYS: ReadonlySet<string> = new Set([
  'q',
  'uploader',
  'sort',
  'lang',
  'inc',
  'exc',
]);

function splitList(value: string | null | undefined): string[] {
  if (!value) return [];
  return value
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function joinList(values: ReadonlyArray<string>): string {
  return values
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .join(',');
}

/**
 * Count the active filter fields in a query (everything except the free-form
 * keyword). Used to badge the Filters button.
 */
export function countActiveFilters(q: SearchQuery): number {
  let n = 0;
  if (q.uploader && q.uploader.trim()) n += 1;
  if (q.sort) n += 1;
  if (q.language) n += 1;
  if (q.includeTags && q.includeTags.length > 0) n += 1;
  if (q.excludeTags && q.excludeTags.length > 0) n += 1;
  if (q.extra) {
    for (const v of Object.values(q.extra)) {
      if (v === undefined || v === null || v === '' || v === false) continue;
      if (Array.isArray(v) && v.length === 0) continue;
      n += 1;
    }
  }
  return n;
}

/**
 * Determine whether a {@link SearchQuery} is "empty" — i.e. no keyword,
 * no tags, no sort, no language, no extras. The source page uses this to
 * decide whether to call `getPopular()` instead of `search()`.
 */
export function isEmptyQuery(q: SearchQuery): boolean {
  if (q.q && q.q.trim()) return false;
  if (q.uploader && q.uploader.trim()) return false;
  if (q.sort) return false;
  if (q.language) return false;
  if (q.includeTags && q.includeTags.length > 0) return false;
  if (q.excludeTags && q.excludeTags.length > 0) return false;
  if (q.extra) {
    for (const v of Object.values(q.extra)) {
      if (v === undefined || v === null || v === '') continue;
      if (Array.isArray(v) && v.length === 0) continue;
      return false;
    }
  }
  return true;
}

/** Parse a `URLSearchParams` into a {@link SearchQuery}, using the schema
 *  to coerce extra-field types (number vs string array). */
export function urlParamsToSearchQuery(
  params: URLSearchParams,
  schema: SearchFiltersSchema | undefined,
): SearchQuery {
  const q: SearchQuery = {};
  const keyword = params.get('q');
  if (keyword) q.q = keyword;
  const uploader = params.get('uploader');
  if (uploader) q.uploader = uploader;
  const sort = params.get('sort');
  if (sort) q.sort = sort;
  const lang = params.get('lang');
  if (lang) q.language = lang;
  const inc = splitList(params.get('inc'));
  if (inc.length > 0) q.includeTags = inc;
  const exc = splitList(params.get('exc'));
  if (exc.length > 0) q.excludeTags = exc;

  const extra: Record<string, unknown> = {};
  if (schema) {
    for (const field of schema.fields) {
      if (RESERVED_KEYS.has(field.id)) continue; // handled above
      const raw = params.get(`${FIELD_PREFIX}${field.id}`);
      if (raw === null || raw === '') continue;
      switch (field.type) {
        case 'number': {
          const n = Number(raw);
          if (Number.isFinite(n)) extra[field.id] = n;
          break;
        }
        case 'toggle': {
          extra[field.id] = raw === '1' || raw === 'true' || raw === 'on';
          break;
        }
        case 'chips': {
          if (field.multi) {
            const vals = splitList(raw);
            if (vals.length > 0) extra[field.id] = vals;
          } else {
            extra[field.id] = raw;
          }
          break;
        }
        case 'tags': {
          const vals = splitList(raw);
          if (vals.length > 0) extra[field.id] = vals;
          break;
        }
        case 'select':
        default:
          extra[field.id] = raw;
      }
    }
  } else {
    for (const [key, raw] of params.entries()) {
      if (!key.startsWith(FIELD_PREFIX) || raw === '') continue;
      const fieldId = key.slice(FIELD_PREFIX.length);
      if (!fieldId || RESERVED_KEYS.has(fieldId)) continue;
      extra[fieldId] = raw;
    }
  }
  if (Object.keys(extra).length > 0) q.extra = extra;

  return q;
}

/** Inverse of {@link urlParamsToSearchQuery}. Produces a fresh
 *  `URLSearchParams` so callers can merge with the existing location's
 *  params if they want. */
export function searchQueryToUrlParams(q: SearchQuery): URLSearchParams {
  const p = new URLSearchParams();
  if (q.q && q.q.trim()) p.set('q', q.q.trim());
  if (q.uploader && q.uploader.trim()) p.set('uploader', q.uploader.trim());
  if (q.sort) p.set('sort', q.sort);
  if (q.language) p.set('lang', q.language);
  if (q.includeTags && q.includeTags.length > 0) p.set('inc', joinList(q.includeTags));
  if (q.excludeTags && q.excludeTags.length > 0) p.set('exc', joinList(q.excludeTags));
  if (q.extra) {
    for (const [k, v] of Object.entries(q.extra)) {
      if (RESERVED_KEYS.has(k)) continue;
      if (v === undefined || v === null) continue;
      if (typeof v === 'boolean') {
        if (v) p.set(`${FIELD_PREFIX}${k}`, '1');
      } else if (typeof v === 'number') {
        if (Number.isFinite(v)) p.set(`${FIELD_PREFIX}${k}`, String(v));
      } else if (Array.isArray(v)) {
        if (v.length > 0) p.set(`${FIELD_PREFIX}${k}`, joinList(v.map(String)));
      } else if (typeof v === 'string') {
        if (v.length > 0) p.set(`${FIELD_PREFIX}${k}`, v);
      }
    }
  }
  return p;
}

/**
 * React hook that exposes the current `SearchQuery` from the URL and a
 * setter that updates the URL without a full reload. Setters merge with
 * the existing URL state on a per-field basis, so a `setFromQuery({})`
 * clears the search entirely (back to popular).
 */
export interface SearchUrlState {
  query: SearchQuery;
  setQuery: (next: SearchQuery, opts?: { replace?: boolean }) => void;
}

export function useSearchUrlState(
  schema: SearchFiltersSchema | undefined,
): SearchUrlState {
  const [params, setParams] = useSearchParams();

  const query = useMemo(
    () => urlParamsToSearchQuery(params, schema),
    [params, schema],
  );

  const setQuery = useCallback(
    (next: SearchQuery, opts?: { replace?: boolean }) => {
      setParams(searchQueryToUrlParams(next), { replace: opts?.replace ?? false });
    },
    [setParams],
  );

  return { query, setQuery };
}
